import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { User } from "@/hooks/useUsers";
import { UserNameField } from "./form/UserNameField";
import { UserEmailField } from "./form/UserEmailField";
import { UserPasswordField } from "./form/UserPasswordField";
import { UserTypeField } from "./form/UserTypeField";
import { UserCompanyField } from "./form/UserCompanyField";
import { useAuth } from "@/hooks/useAuth";

type UserFormData = Omit<User, "id">;

interface UserFormProps {
  onSubmit: (data: UserFormData) => void;
  initialData?: User;
  onClear: () => void;
  isEditing: boolean;
}

const defaultValues: UserFormData = {
  name: "",
  email: "",
  type: "",
  password: "",
  companyId: "",
};

export const UserForm = ({ onSubmit, initialData, onClear, isEditing }: UserFormProps) => {
  const { currentUser } = useAuth();
  const isSuperAdmin = currentUser?.type === 'super_admin';
  const isAdmin = currentUser?.type === 'admin';

  const form = useForm<UserFormData>({
    defaultValues: initialData ? {
      name: initialData.name,
      email: initialData.email,
      type: initialData.type,
      password: initialData.password || "",
      companyId: initialData.companyId || "",
    } : defaultValues,
  });

  useEffect(() => {
    if (initialData) {
      form.reset({
        name: initialData.name,
        email: initialData.email,
        type: initialData.type,
        password: initialData.password || "",
        companyId: initialData.companyId || "",
      });
    } else {
      form.reset(defaultValues);
    }
  }, [initialData, form]);

  const handleFormSubmit = (data: UserFormData) => {
    // Se não for super admin, força o vínculo com a empresa do usuário atual
    if (!isSuperAdmin) {
      data.companyId = currentUser?.companyId || "";
    }
    onSubmit(data);
    if (!isEditing) {
      form.reset(defaultValues);
    }
  };

  const handleClear = () => {
    form.reset(defaultValues);
    onClear();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <UserNameField form={form} />
          <UserEmailField form={form} />
          <UserPasswordField form={form} isEditing={isEditing} />
          <UserTypeField form={form} isSuperAdmin={isSuperAdmin} isAdmin={isAdmin} />
          <UserCompanyField form={form} />
        </div>

        <div className="flex justify-end space-x-2">
          <Button type="submit">
            {isEditing ? "Atualizar" : "Salvar"}
          </Button>
          <Button type="button" variant="outline" onClick={handleClear}>
            Limpar
          </Button>
        </div>
      </form>
    </Form>
  );
};